import { isMenuItemWithComponent, isNestedMenuItemWithText } from './types';

import type { MenuProps } from './Menu';
import type { MenuItemType, MenuItemWithText } from './types';
import type { PopoverOrigin } from '@mui/material';
import type { MenuAlign, MenuOption, MenuSide } from 'components/Menu';

/**
 * Maps the legacy MUI `Menu` props onto the Design System 2.0 API.
 *
 * TODO(HNS-264): temporary, exactly like `Buttons/IconButton/toDsIconProps`. It exists so the visual
 * refresh is gated inside the wrapper rather than at the call sites; retiring the flag deletes it.
 *
 * Returning `null` means "DS 2.0 cannot express this menu" and sends the caller back to MUI. Note
 * the granularity: a refusal takes the WHOLE menu back, never a single row. A panel showing two
 * refreshed rows with the third silently missing is worse than a panel that has not been refreshed
 * yet, and a per-row fallback cannot mix Base UI and MUI items inside one popup anyway.
 */
type DsProps = { anchor: Element | null; options: MenuOption[]; side: MenuSide; align: MenuAlign };

/**
 * MUI's `anchorEl` also accepts a thunk and a `PopoverVirtualElement` (a bare
 * `getBoundingClientRect`), which is how it supports anchoring to a text selection or a point.
 *
 * Narrowed HERE rather than cast at the call site, for the same reason `toDsIconProps` carries the
 * `aria-label` out in its result: this mapper is what proves the value is usable, so it should also
 * be what hands it over already typed. Base UI would in fact take a virtual element too, but the DS
 * component deliberately does not expose one — no call site anchors to anything but an element, and
 * an untested path is worse than a refused one.
 */
const isElementAnchor = (anchor: MenuProps['anchor']): anchor is Element | null | undefined =>
  anchor === null || anchor === undefined || anchor instanceof Element;

/**
 * MUI's default origins, restated because `Menu` applies them internally — so a caller that passes
 * neither still gets `bottom/center` -> `top/center`, and the mapper has to agree with that rather
 * than treat "absent" as its own case.
 */
const DEFAULT_ANCHOR_ORIGIN: PopoverOrigin = { vertical: 'bottom', horizontal: 'center' };
const DEFAULT_TRANSFORM_ORIGIN: PopoverOrigin = { vertical: 'top', horizontal: 'center' };

const ALIGN: Record<string, MenuAlign> = { left: 'start', center: 'center', right: 'end' };

/**
 * MUI positions a popover by naming a point on the anchor AND a point on the popover, then putting
 * the two together. Base UI names one side and one alignment. The two models agree only when the
 * vertical origins are COMPLEMENTARY — the popover's top edge against the anchor's bottom edge, or
 * the reverse — which is what "stacked below" and "stacked above" mean.
 *
 * Everything else is refused rather than approximated:
 *
 *   - matching verticals (`bottom`/`bottom`) overlap the panel onto the anchor, which Base UI's
 *     side-plus-align model has no way to say.
 *   - disagreeing horizontals shift the panel sideways by an arbitrary amount; `alignOffset` could
 *     fake it, but only by computing a pixel value from the anchor's width at render time.
 *   - a `center` vertical is how MUI expresses a menu opening to the SIDE of its anchor. That is a
 *     real Base UI placement (`side: 'left' | 'right'`), but the only call sites using it are nested
 *     submenus, which are refused below anyway — so mapping it would add an untested path.
 *   - numeric origins are raw pixel offsets with no equivalent at all.
 */
const toSideAndAlign = (
  anchorOrigin: PopoverOrigin,
  transformOrigin: PopoverOrigin
): Pick<DsProps, 'side' | 'align'> | null => {
  if (anchorOrigin.horizontal !== transformOrigin.horizontal) {
    return null;
  }

  const align = typeof anchorOrigin.horizontal === 'string' ? ALIGN[anchorOrigin.horizontal] : undefined;

  if (!align) {
    return null;
  }

  if (anchorOrigin.vertical === 'bottom' && transformOrigin.vertical === 'top') {
    return { side: 'bottom', align };
  }

  if (anchorOrigin.vertical === 'top' && transformOrigin.vertical === 'bottom') {
    return { side: 'top', align };
  }

  return null;
};

/**
 * `severity: 'danger'` is the documented way to mark a destructive row; `color: 'danger'` is an
 * older escape hatch that `MenuItem` still honours (its `color` is typed `({} & string) | 'danger'`,
 * so anything goes). Both are read here so a row cannot look red on MUI and neutral in DS.
 *
 * Any OTHER `color` value is ignored rather than refused: it is a free-form string that the legacy
 * `MenuItem` forwards to MUI's `color` prop, where `MuiMenuItem` does nothing with it. So it has no
 * visual effect today and there is nothing to preserve.
 */
const isDestructive = ({ severity, color }: MenuItemWithText) => severity === 'danger' || color === 'danger';

const toOption = (item: MenuItemType): MenuOption | null => {
  /**
   * An arbitrary `ReactNode` as a row. Refused because the node is authored against MUI — the call
   * sites pass `<Typography>`, and one passes a whole `<CommunitySelectorItem>` — so rendering it
   * inside a Tailwind row would mix two type scales in one panel. None of these are on the event
   * page; they are MasterAdmin, the EventWizard and the sidebar's community switcher.
   */
  if (isMenuItemWithComponent(item)) {
    return null;
  }

  /**
   * A submenu. Base UI has `Menu.SubmenuRoot` and `Menu.SubmenuTrigger`, so this is buildable — but
   * the design draws no submenu cell to build it against, and there is exactly one call site
   * (`useEventArtifactsMenu`, the event recordings list). Worth knowing: that one IS reachable from
   * the event page's own "more actions" menu once an event has recordings, so this menu refreshes
   * for an event without recordings and stays on MUI for one with them.
   */
  if (isNestedMenuItemWithText(item)) {
    return null;
  }

  return {
    /**
     * The label, not the array index. These arrays are assembled from conditional spreads
     * (`...(canDelete ? [x] : [])`), so indices shift as privileges resolve and an index key would
     * hand one action's DOM node to another. A `data-testid` is preferred where the call site set
     * one, since it is chosen to be stable.
     */
    id: item['data-testid'] ?? item.text,
    label: item.text,
    /**
     * Carried across under the same name. It is the reason the host join choice can refresh at all:
     * its rows used to be `component` nodes, which this mapper refuses. See `MenuItemWithText`.
     */
    description: item.description,
    icon: item.icon,
    tone: isDestructive(item) ? 'destructive' : 'default',
    disabled: item.disabled,
    onSelect: item.onClick,
    // Deliberately carried as-is: `withDivider` OPENS a group, and so does `separatorBefore`.
    separatorBefore: item.withDivider,
    /**
     * Handed over as well as used for `id`, so a row keeps its test id after the swap. Without it,
     * every call site that queries a row by test id would break the moment the menu refreshed —
     * which is not a behaviour change anyone would notice until a suite went red.
     */
    'data-testid': item['data-testid'],
  };
};

export const toDsProps = ({
  anchor,
  items,
  activeIndex,
  anchorOrigin = DEFAULT_ANCHOR_ORIGIN,
  transformOrigin = DEFAULT_TRANSFORM_ORIGIN,
  sx,
  slotProps,
  disableScrollLock,
}: MenuProps): DsProps | null => {
  if (!isElementAnchor(anchor)) {
    return null;
  }

  /**
   * `sx` and `slotProps` are Emotion-shaped props with no effect on a Tailwind panel — accepting
   * either would drop the styles silently rather than fail. `slotProps` is the one that bites in
   * practice: `ActionMenuButton` and `CommunitySelectorMenu` use it to set the panel's width.
   *
   * `disableScrollLock` maps cleanly onto Base UI's `modal={false}`, but no call site passes it, so
   * it is refused rather than shipped untested.
   */
  if (sx || slotProps || disableScrollLock) {
    return null;
  }

  /**
   * A selected row. The drawn component HAS one — it is a selection list — but this component
   * deliberately does not, because an action menu's rows run a command and close. The single caller
   * (`RoomQuestionAnswerAuthorSelect`) is using the menu as a value picker, which is what `Select`
   * is for.
   */
  if (activeIndex !== undefined) {
    return null;
  }

  const placement = toSideAndAlign(anchorOrigin, transformOrigin);

  if (!placement) {
    return null;
  }

  const options = items.map(toOption);

  if (options.some((option) => option === null)) {
    return null;
  }

  return { ...placement, anchor: anchor ?? null, options: options as MenuOption[] };
};
